/* ═══════════════════════════════════════════════════════════════
   TeamPanel — assign members/teams, change role, remove (Sheet).
   The available-member picker uses Explorer multi-select semantics
   (click = select one, Ctrl/Cmd+click = toggle, Shift+click = range)
   per docs/DESIGN.md's multi-select law — never a native <select>.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Icon } from '../../../components/ui/Icon';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { rem } from '../../../lib/rem';
import type { ProjectAssignment } from '../../../../shared/entities';
import type { ProjectWidgetCtx } from '../widgets/context';
import { assignableTeamMembers } from '../widgets/teamAssignable';

const ROLE_OPTIONS = [
  { value: 'pm', label: 'PM' },
  { value: 'pic', label: 'PIC' },
  { value: 'member', label: 'Member' },
];

export function TeamPanel({
  ctx,
  open,
  onClose,
}: {
  ctx: ProjectWidgetCtx;
  open: boolean;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [teamPick, setTeamPick] = useState('');
  const [pendingRemove, setPendingRemove] = useState<ProjectAssignment | null>(null);

  // Panel stays mounted across close/open (focus-restore fix) — reset
  // ephemeral UI state (never a draft to resync from props) each time it
  // opens rather than relying on a remount to clear it.
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(new Set());
      setAnchor(null);
      setTeamPick('');
      setPendingRemove(null);
    }
  }, [open]);

  // Radix's Dialog only restores focus to a <Dialog.Trigger> ref on close —
  // this kit's Sheets are always opened imperatively from a page-owned
  // button, never a <SheetTrigger>, so that ref is always null and focus
  // silently lands on <body> (DESIGN.md §7). Capture whatever was focused
  // right before open and restore it ourselves via onCloseAutoFocus.
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
  }, [open]);

  const assignedIds = new Set(ctx.assignments.data.map((a) => a.teamMemberId));
  // Decision 11 — the "who may I assign" filter (shared with the Team
  // widget's add-person row so neither surface offers a person the server
  // will refuse), further narrowed to this panel's search box.
  const available = assignableTeamMembers(ctx, assignedIds).filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  function clickMember(id: string, idx: number, e: React.MouseEvent) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (e.shiftKey && anchor !== null) {
        const [lo, hi] = anchor < idx ? [anchor, idx] : [idx, anchor];
        for (let i = lo; i <= hi; i++) next.add(available[i].id);
        return next;
      }
      if (e.ctrlKey || e.metaKey) {
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setAnchor(idx);
        return next;
      }
      setAnchor(idx);
      return new Set([id]);
    });
  }

  async function assignSelected() {
    if (selected.size === 0 || assigning) return;
    setAssigning(true);
    try {
      await Promise.allSettled(Array.from(selected).map((id) => ctx.assignMember(id, 'member')));
      setSelected(new Set());
    } finally {
      setAssigning(false);
    }
  }

  const assignedTeamIds = new Set(
    ctx.teamAssignments.filter((ta) => assignedIds.has(ta.teamMemberId)).map((ta) => ta.teamId)
  );

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="right"
          className="!w-[clamp(26rem,40vw,44rem)]"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>Manage team</SheetTitle>
          </SheetHeader>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
              marginTop: 'var(--space-4)',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Currently assigned
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: rem(2) }}>
                {ctx.assignments.data.length === 0 && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      fontStyle: 'italic',
                    }}
                  >
                    No one assigned yet.
                  </div>
                )}
                {ctx.assignments.data.map((a) => {
                  const m = ctx.members.find((mm) => mm.id === a.teamMemberId);
                  if (!m) return null;
                  return (
                    <div
                      key={a.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: `${rem(4)} 0`,
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          fontSize: 'var(--font-size-base)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.name}
                      </span>
                      <div style={{ width: rem(100) }}>
                        <SearchableSelect
                          value={a.role || 'member'}
                          options={ROLE_OPTIONS}
                          onChange={(v) => ctx.setAssignmentRole(a.id, v || 'member')}
                          clearable={false}
                          disabled={!ctx.can.assignTeam}
                        />
                      </div>
                      {ctx.can.assignTeam && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${m.name} from project`}
                          onClick={() => setPendingRemove(a)}
                        >
                          <Icon icon={X} size="sm" className="text-danger" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                + Assign team
              </div>
              <SearchableSelect
                value={teamPick}
                options={ctx.teams.map((t) => {
                  const memberIds = ctx.teamAssignments
                    .filter((ta) => ta.teamId === t.id)
                    .map((ta) => ta.teamMemberId);
                  const newCount = memberIds.filter((id) => !assignedIds.has(id)).length;
                  return {
                    value: t.id,
                    label: t.name + (t.shortCode ? ` (${t.shortCode})` : ''),
                    hint:
                      memberIds.length === 0
                        ? 'no members'
                        : newCount === 0
                          ? 'all assigned'
                          : `+${newCount} new`,
                  };
                })}
                onChange={(v) => {
                  setTeamPick('');
                  if (v) void ctx.assignTeam(v);
                }}
                placeholder="Search teams…"
                disabled={!ctx.can.assignTeam || assignedTeamIds.size === ctx.teams.length}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                + Assign members
              </div>
              <Input
                placeholder="Search members…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 'var(--space-2)' }}
              />
              <div
                role="listbox"
                aria-multiselectable="true"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: rem(2),
                  maxHeight: rem(220),
                  overflow: 'auto',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-md)',
                  padding: rem(4),
                }}
              >
                {available.length === 0 && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      fontStyle: 'italic',
                      padding: 'var(--space-2)',
                      textAlign: 'center',
                    }}
                  >
                    No available members
                  </div>
                )}
                {available.map((m, idx) => {
                  const isSelected = selected.has(m.id);
                  return (
                    <div
                      key={m.id}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                      onClick={(e) => clickMember(m.id, idx, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          clickMember(m.id, idx, e as unknown as React.MouseEvent);
                        }
                      }}
                      style={{
                        padding: `${rem(5)} ${rem(10)}`,
                        borderRadius: rem(5),
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-base)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        background: isSelected ? 'rgba(var(--acr), 0.14)' : 'transparent',
                      }}
                    >
                      <span
                        style={{
                          width: rem(26),
                          height: rem(26),
                          borderRadius: '50%',
                          background: 'var(--ac)',
                          color: 'var(--color-text-inverse)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {(m.name || '?')[0]}
                      </span>
                      <span>{m.name || 'Unnamed'}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 'var(--space-2)' }}>
                <Button
                  size="sm"
                  onClick={assignSelected}
                  disabled={selected.size === 0 || assigning || !ctx.can.assignTeam}
                >
                  {assigning
                    ? 'Assigning…'
                    : `Assign selected${selected.size > 0 ? ` (${selected.size})` : ''}`}
                </Button>
              </div>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!pendingRemove}
        onOpenChange={(o) => !o && setPendingRemove(null)}
        title="Remove from project?"
        description="This member loses access to this project's assignment. Their logged time is unaffected."
        confirmLabel="Remove"
        destructive
        onConfirm={async () => {
          if (!pendingRemove) return;
          await ctx.removeAssignment(pendingRemove.id);
          setPendingRemove(null);
        }}
      />
    </>
  );
}
